/**
 * FP_USER Inspector surface registry.
 *
 * Single source of truth for which Inspector tools, Data Explorer tabs, and
 * top-level pages are exposed to FP_USER accounts on the frontend. Staff
 * roles (EVO_ADMIN / EVO_TEAM / PSC_TEAM) are not affected by this registry.
 *
 * Mirrored in `backend/api/fp_user_access.py` — keep the two files in sync.
 */

export const FP_USER_ALLOWED_TOOLS: ReadonlySet<string> = new Set([
  'product_catalogue_review',
  'cogs_review',
  'invoice_payments_export',
])

// Data Explorer browse surfaces (sales/clients/products/invoices/inventory)
// remain hidden from FP_USER. 'sync_status' is allowed because the Inspector
// header chip strip needs a "last synced N hours ago" timestamp for the
// FP's own data - same payload staff sees, no PII or financial detail.
export const FP_USER_ALLOWED_DATA_TYPES: ReadonlySet<string> = new Set(['sync_status'])

export const FP_USER_ALLOWED_PAGES: ReadonlySet<string> = new Set([
  'support',
])

export type FpUserAccessKind = 'tool' | 'data' | 'page'

export function fpUserCanAccess(kind: FpUserAccessKind, id: string): boolean {
  switch (kind) {
    case 'tool':
      return FP_USER_ALLOWED_TOOLS.has(id)
    case 'data':
      return FP_USER_ALLOWED_DATA_TYPES.has(id)
    case 'page':
      return FP_USER_ALLOWED_PAGES.has(id)
  }
}

/**
 * Returns true if the user is allowed to see the given Inspector item.
 * Staff roles always pass through. FP_USER is checked against the registry.
 */
export function userCanAccessInspectorItem(
  role: string | undefined,
  kind: FpUserAccessKind,
  id: string,
): boolean {
  if (role !== 'FP_USER') return true
  return fpUserCanAccess(kind, id)
}
